import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Lock, Globe } from 'lucide-react';
import type { GuildSummary } from '@/lib/api';
import { GuildLevelBadge } from './GuildLevelBadge';

interface GuildCardProps {
  guild: GuildSummary;
}

export function GuildCard({ guild }: GuildCardProps) {
  // Note: If adding interactive buttons inside this card, move the Link to a
  // specific clickable area to avoid nested interactive elements (a11y issue).
  return (
    <Link to={`/a/arche_guilds/${guild.guildId}`} className="block">
      <Card className="h-full hover:border-primary/50 transition-colors">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base line-clamp-1">{guild.name}</CardTitle>
            <div className="flex gap-1 shrink-0">
              <GuildLevelBadge level={guild.level} />
              {guild.guildType === 'earning' && (
                <Badge variant="secondary" className="text-xs">
                  Earning
                </Badge>
              )}
              {guild.visibility === 'private' ? (
                <Badge variant="outline" className="text-xs">
                  <Lock className="h-3 w-3 mr-1" />
                  Private
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">
                  <Globe className="h-3 w-3 mr-1" />
                  Public
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {guild.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{guild.description}</p>
          )}
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="h-4 w-4 mr-1" />
            {guild.memberCount} {guild.memberCount === 1 ? 'member' : 'members'}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
